import React, { useState, useEffect } from 'react';
import { Card, Statistic, Table, Tag, Badge, Spin, Empty, Row, Col, Progress } from 'antd';
import { MetricCard, HeadlineCard } from '../../components/insights';
import {
  Users,
  UserMinus,
  IndianRupee,
  Target,
} from 'lucide-react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import dayjs from 'dayjs';
import useApiMethods from '../../lib/useApiMethods';
import { HRMS_DATA, EMPLOYEE_PERFORMANCE } from '../../constants/api';
import ExportButton from '../../components/common/ExportButton';
import { flattenForExport } from '../../lib/exportUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Filler, Tooltip, Legend);

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const HROverview = () => {
  const { fetch } = useApiMethods();
  const [data, setData] = useState(null);
  const [empPerformance, setEmpPerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const [res, perfRes] = await Promise.all([
        fetch(HRMS_DATA, {}, setLoading),
        fetch(EMPLOYEE_PERFORMANCE),
      ]);
      if (res?.status !== 'fail') {
        setData(res?.data || res);
      }
      if (perfRes?.status !== 'fail') {
        const pd = perfRes?.data || perfRes;
        setEmpPerformance(Array.isArray(pd) ? pd : []);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Empty description="Unable to load HR data" className="mt-20" />;
  }

  const {
    headcount = 0,
    attrition_rate = 0,
    salary_total: monthly_salary = 0,
    headcount_by_dept: raw_dept,
    employees: raw_employees,
    attendance_today: raw_today,
    attendance_trend: raw_trend,
    reimbursements: raw_reimb,
    advance_salary: raw_adv,
  } = data;

  const reimb = raw_reimb || {};
  const adv = raw_adv || {};

  const employees = raw_employees || [];
  const attendanceToday = raw_today || {};
  const attendanceTrend = Array.isArray(raw_trend) ? raw_trend : [];

  const department_headcount = Array.isArray(raw_dept)
    ? raw_dept
    : raw_dept ? Object.entries(raw_dept).map(([department, count]) => ({ department, count })) : [];

  // Headcount by Department chart
  const deptChartData = {
    labels: department_headcount.map((d) => d.department),
    datasets: [
      {
        label: 'Headcount',
        data: department_headcount.map((d) => d.count),
        backgroundColor: [
          '#6366f1', '#10b981', '#f59e0b', '#ef4444',
          '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
        ],
        borderRadius: 6,
        barThickness: 32,
      },
    ],
  };

  const deptChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.x} employees`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(148,163,184,0.18)' },
        ticks: { stepSize: 5 },
      },
      y: { grid: { display: false } },
    },
  };


  // ── Today's attendance donut ───────────────────────────────────────────
  const breakdown = attendanceToday.status_breakdown || {};
  const todayPresent = breakdown.present ?? attendanceToday.present_count ?? 0;
  const todayAbsent = breakdown.absent ?? attendanceToday.absent_count ?? 0;
  const todayLeave = breakdown.leave ?? attendanceToday.on_leave_count ?? 0;
  const todayHalfDay = breakdown.half_day ?? attendanceToday.half_day_count ?? 0;
  const todayMarked = attendanceToday.total_marked ?? 0;
  const todayNotMarked = Math.max(0, headcount - todayMarked);

  const attendanceTodayData = {
    labels: ['Present', 'Absent', 'Leave', 'Half Day', 'Not Marked'],
    datasets: [
      {
        data: [todayPresent, todayAbsent, todayLeave, todayHalfDay, todayNotMarked],
        backgroundColor: ['#10b981', '#ef4444', '#f59e0b', '#8b5cf6', '#e5e7eb'],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  const attendanceTodayOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 12 } } },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed}`,
        },
      },
    },
  };

  // ── 30-day attendance trend line chart ─────────────────────────────────
  const trendLabels = attendanceTrend.map((r) => dayjs(r.date).format('DD MMM'));
  const trendPcts = attendanceTrend.map((r) => r.attendance_pct || 0);

  const attendanceTrendData = {
    labels: trendLabels,
    datasets: [
      {
        label: 'Attendance %',
        data: trendPcts,
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.15)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#6366f1',
      },
    ],
  };

  const attendanceTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 } },
      y: {
        beginAtZero: true,
        max: 100,
        grid: { color: 'rgba(148,163,184,0.18)' },
        ticks: { callback: (v) => `${v}%`, stepSize: 25 },
      },
    },
  };

  // Salary by department
  const salaryByDept = employees.reduce((acc, e) => {
    const dept = e.department || 'Unassigned';
    acc[dept] = (acc[dept] || 0) + (e.monthly_ctc || 0);
    return acc;
  }, {});
  const salaryDeptLabels = Object.keys(salaryByDept);
  const salaryByDeptData = {
    labels: salaryDeptLabels,
    datasets: [
      {
        label: 'Monthly Salary',
        data: salaryDeptLabels.map((d) => salaryByDept[d]),
        backgroundColor: '#6366f1',
        borderRadius: 6,
        barThickness: 28,
      },
    ],
  };
  const salaryByDeptOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${formatINR(ctx.parsed.x)}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(148,163,184,0.18)' },
        ticks: {
          callback: (val) =>
            val >= 100000 ? `${(val / 100000).toFixed(1)}L` : `${(val / 1000).toFixed(0)}k`,
        },
      },
      y: { grid: { display: false } },
    },
  };

  // Attendance performance buckets
  const buckets = { '90-100%': 0, '70-89%': 0, '50-69%': 0, '<50%': 0 };
  employees.forEach((e) => {
    const pct = e.attendance?.attendance_pct || 0;
    if (pct >= 90) buckets['90-100%']++;
    else if (pct >= 70) buckets['70-89%']++;
    else if (pct >= 50) buckets['50-69%']++;
    else buckets['<50%']++;
  });
  const performanceData = {
    labels: Object.keys(buckets),
    datasets: [
      {
        label: 'Employees',
        data: Object.values(buckets),
        backgroundColor: ['#10b981', '#84cc16', '#f59e0b', '#ef4444'],
        borderRadius: 6,
        barThickness: 42,
      },
    ],
  };
  const performanceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y} employees`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(148,163,184,0.18)' },
        ticks: { stepSize: 1, precision: 0 },
      },
    },
  };

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Human Resources</h2>
        <p className="text-sm text-muted-foreground">Employee management and HR metrics from HRMS</p>
      </div>

      {/* Headline */}
      <HeadlineCard
        state={attrition_rate > 15 ? 'critical' : attrition_rate > 8 ? 'warning' : 'healthy'}
        title={
          attrition_rate > 15
            ? `Attrition at ${attrition_rate}% — significantly above threshold`
            : attrition_rate > 8
              ? `Attrition at ${attrition_rate}% — monitor closely`
              : `Team stable at ${headcount} with ${attrition_rate}% attrition`
        }
        description={`${headcount} active employees across ${department_headcount.length} departments · ${formatINR(monthly_salary)} monthly payroll`}
        metrics={[
          { label: 'Headcount', value: headcount },
          { label: 'Attrition', value: `${attrition_rate}%` },
          { label: 'Payroll', value: formatINR(monthly_salary) },
          { label: 'Departments', value: department_headcount.length },
        ]}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          icon={Users}
          intent="primary"
          title="Total Headcount"
          value={headcount}
        />
        <MetricCard
          icon={UserMinus}
          intent={attrition_rate > 15 ? 'negative' : attrition_rate > 8 ? 'warning' : 'positive'}
          title="Attrition Rate"
          value={`${attrition_rate}%`}
          subtitle={attrition_rate > 15 ? 'Above threshold' : attrition_rate > 8 ? 'Monitor' : 'Within range'}
          goodUp={false}
        />
        <MetricCard
          icon={IndianRupee}
          intent="positive"
          title="Monthly Salary"
          value={formatINR(monthly_salary)}
        />
      </div>

      {/* Payroll sub-section — Reimbursements + Advance Salary */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Payroll & Advances</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={IndianRupee}
            intent={(reimb.pending_count || 0) > 0 ? 'warning' : 'neutral'}
            title="Reimbursements Pending"
            value={reimb.pending_count || 0}
            subtitle={`${formatINR(reimb.pending_amount || 0)} requested`}
            goodUp={false}
          />
          <MetricCard
            icon={IndianRupee}
            intent="positive"
            title="Reimb. Paid This Month"
            value={formatINR(reimb.approved_this_month_amount || 0)}
            subtitle={`${reimb.approved_this_month_count || 0} approved requests`}
          />
          <MetricCard
            icon={IndianRupee}
            intent={(adv.pending_count || 0) > 0 ? 'warning' : 'neutral'}
            title="Advance Salary Pending"
            value={adv.pending_count || 0}
            subtitle={`${formatINR(adv.pending_amount || 0)} requested`}
            goodUp={false}
          />
          <MetricCard
            icon={IndianRupee}
            intent="positive"
            title="Adv. Disbursed This Month"
            value={formatINR(adv.approved_this_month_amount || 0)}
            subtitle={`${adv.approved_this_month_count || 0} employees`}
          />
        </div>
      </div>

      {/* Headcount by Department */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <h4 className="font-semibold text-foreground mb-4">Headcount by Department</h4>
        {department_headcount.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
          <div style={{ height: 300 }}><Bar data={deptChartData} options={deptChartOptions} /></div>
        )}
      </div>

      {/* Attendance & Salary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Today's Attendance <span className="text-xs text-muted-foreground font-normal">({dayjs().format('DD MMM')})</span></h4>
          {headcount === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 260 }}><Doughnut data={attendanceTodayData} options={attendanceTodayOptions} /></div>
          )}
        </div>
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Attendance Distribution</h4>
          {employees.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 260 }}><Bar data={performanceData} options={performanceOptions} /></div>
          )}
        </div>
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Salary by Department</h4>
          {salaryDeptLabels.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 260 }}><Bar data={salaryByDeptData} options={salaryByDeptOptions} /></div>
          )}
        </div>
      </div>

      {/* Attendance Trend */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <h4 className="font-semibold text-foreground mb-4">Attendance Trend — Last 30 Days</h4>
        {attendanceTrend.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
          <div style={{ height: 260 }}><Line data={attendanceTrendData} options={attendanceTrendOptions} /></div>
        )}
      </div>

      {/* Employee Performance */}
      {empPerformance.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-violet-500" />
            <h4 className="font-semibold text-foreground">Employee Performance</h4>
            <span className="text-xs text-muted-foreground ml-auto mr-3">{empPerformance.length} scored</span>
            <ExportButton
              data={flattenForExport(empPerformance, { Name: 'name', Score: 'score', Band: 'band', 'Monthly CTC': 'monthly_ctc', Tickets: 'metrics.tickets_total', Deals: 'metrics.deals_count', 'Attendance %': 'metrics.attendance_pct' })}
              filename="employee-performance"
              pdfColumns={[{title:'Name',dataKey:'Name'},{title:'Score',dataKey:'Score'},{title:'Band',dataKey:'Band'},{title:'CTC',dataKey:'Monthly CTC'},{title:'Tickets',dataKey:'Tickets'},{title:'Attendance',dataKey:'Attendance %'}]}
              pdfTitle="Employee Performance Report"
            />
          </div>
          <Table
            dataSource={empPerformance}
            rowKey="id"
            pagination={{ pageSize: 15, showSizeChanger: true }}
            size="middle"
            expandable={{
              expandedRowRender: (record) => (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
                  {Object.entries(record.breakdown || {}).map(([key, val]) => (
                    <div key={key}>
                      <div className="text-xs text-muted-foreground mb-1">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
                      <Progress percent={Math.round(val)} size="small" strokeColor={val >= 70 ? '#10b981' : val >= 40 ? '#f59e0b' : '#ef4444'} />
                    </div>
                  ))}
                </div>
              ),
            }}
            columns={[
              {
                title: 'Employee', dataIndex: 'name', key: 'name',
                render: (text, r) => (
                  <div>
                    <span className="font-medium">{text}</span>
                    <div className="flex gap-1 mt-1">
                      {r.linked.map(s => (
                        <span key={s} className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                          s === 'hrms' ? 'bg-purple-50 dark:bg-purple-950/30 text-purple-600' : s === 'crm' ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-600' : 'bg-green-50 dark:bg-green-950/30 text-green-600'
                        }`}>{s.toUpperCase()}</span>
                      ))}
                    </div>
                  </div>
                ),
              },
              {
                title: 'Score', dataIndex: 'score', key: 'score', width: 80,
                sorter: (a, b) => a.score - b.score,
                render: (score) => (
                  <Badge count={score} overflowCount={100} showZero
                    style={{ backgroundColor: score >= 80 ? '#10b981' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#ef4444', fontWeight: 700 }}
                  />
                ),
              },
              {
                title: 'Band', dataIndex: 'band', key: 'band',
                filters: [
                  { text: 'Excellent', value: 'Excellent' },
                  { text: 'Good', value: 'Good' },
                  { text: 'Average', value: 'Average' },
                  { text: 'Needs Improvement', value: 'Needs Improvement' },
                ],
                onFilter: (value, record) => record.band === value,
                render: (band) => {
                  const colorMap = { Excellent: 'green', Good: 'blue', Average: 'orange', 'Needs Improvement': 'red' };
                  return <Tag color={colorMap[band] || 'default'}>{band}</Tag>;
                },
              },
              {
                title: 'Monthly CTC', dataIndex: 'monthly_ctc', key: 'ctc',
                render: (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '-',
                sorter: (a, b) => (a.monthly_ctc || 0) - (b.monthly_ctc || 0),
              },
              {
                title: 'Key Output', key: 'output',
                render: (_, r) => {
                  if (r.metrics.tickets_total > 0) return <span>{r.metrics.tickets_total - r.metrics.tickets_pending} done / {r.metrics.tickets_total} tickets</span>;
                  if (r.metrics.deals_count > 0) return <span>{r.metrics.deals_count} closed (₹{(r.metrics.pipeline_value / 100000).toFixed(1)}L)</span>;
                  return <span>{r.metrics.attendance_pct}% attendance</span>;
                },
              },
              {
                title: 'Cost Efficiency', key: 'efficiency',
                render: (_, r) => {
                  if (!r.cost_efficiency) return <span className="text-gray-300">-</span>;
                  return <span>{r.cost_efficiency.label}</span>;
                },
              },
            ]}
          />
        </div>
      )}

      {/* Employees Table */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-indigo-500" />
          <h4 className="font-semibold text-foreground">Employees ({employees.length})</h4>
          <span className="text-xs text-muted-foreground ml-auto mr-3">{dayjs().format('MMMM YYYY')}</span>
          <ExportButton
            data={flattenForExport(employees, { 'Employee ID': 'employee_id', Name: 'name', Department: 'department', Designation: 'designation', Email: 'email', 'DOJ': 'date_of_joining', 'Attendance %': 'attendance.attendance_pct', Present: 'attendance.present_days', Absent: 'attendance.absent_days', Leave: 'attendance.leave_days', 'Monthly CTC': 'monthly_ctc', 'Net Salary': 'net_salary', 'Annual CTC': 'annual_ctc' })}
            filename="employees"
            pdfColumns={[{title:'ID',dataKey:'Employee ID'},{title:'Name',dataKey:'Name'},{title:'Dept',dataKey:'Department'},{title:'Attendance %',dataKey:'Attendance %'},{title:'Monthly CTC',dataKey:'Monthly CTC'},{title:'Net Salary',dataKey:'Net Salary'}]}
            pdfTitle="Employee Report"
          />
        </div>
        <Table
          dataSource={employees}
          rowKey="id"
          pagination={{ pageSize: 15, showSizeChanger: true }}
          size="middle"
          scroll={{ x: 1200 }}
          locale={{ emptyText: <Empty description="No employees" /> }}
          columns={[
            {
              title: 'Employee ID',
              dataIndex: 'employee_id',
              key: 'employee_id',
              width: 120,
              fixed: 'left',
            },
            {
              title: 'Name',
              dataIndex: 'name',
              key: 'name',
              width: 180,
              fixed: 'left',
              render: (text) => <span className="font-medium">{text}</span>,
            },
            {
              title: 'Department',
              dataIndex: 'department',
              key: 'department',
              width: 140,
              filters: Array.from(new Set(employees.map(e => e.department).filter(Boolean)))
                .map(d => ({ text: d, value: d })),
              onFilter: (value, record) => record.department === value,
            },
            {
              title: 'Designation',
              dataIndex: 'designation',
              key: 'designation',
              width: 160,
            },
            {
              title: 'Type',
              dataIndex: 'employee_type',
              key: 'employee_type',
              width: 110,
              render: (type) => type ? <Tag>{type}</Tag> : '-',
            },
            {
              title: 'Email',
              dataIndex: 'email',
              key: 'email',
              width: 200,
              ellipsis: true,
            },
            {
              title: 'DOJ',
              dataIndex: 'date_of_joining',
              key: 'date_of_joining',
              width: 110,
              render: (val) => val ? dayjs(val).format('DD MMM YYYY') : '-',
              sorter: (a, b) => (a.date_of_joining || '').localeCompare(b.date_of_joining || ''),
            },
            {
              title: 'Attendance %',
              dataIndex: ['attendance', 'attendance_pct'],
              key: 'attendance_pct',
              width: 130,
              align: 'right',
              render: (val) => {
                const pct = val || 0;
                const color = pct >= 90 ? '#10b981' : pct >= 70 ? '#f59e0b' : '#ef4444';
                return <span style={{ color, fontWeight: 600 }}>{pct}%</span>;
              },
              sorter: (a, b) => (a.attendance?.attendance_pct || 0) - (b.attendance?.attendance_pct || 0),
            },
            {
              title: 'Present',
              dataIndex: ['attendance', 'present_days'],
              key: 'present_days',
              width: 90,
              align: 'right',
              render: (val) => <span className="text-emerald-600 dark:text-emerald-400">{val || 0}</span>,
            },
            {
              title: 'Absent',
              dataIndex: ['attendance', 'absent_days'],
              key: 'absent_days',
              width: 90,
              align: 'right',
              render: (val) => <span className="text-red-500">{val || 0}</span>,
            },
            {
              title: 'Leave',
              dataIndex: ['attendance', 'leave_days'],
              key: 'leave_days',
              width: 90,
              align: 'right',
              render: (val) => <span className="text-amber-600 dark:text-amber-400">{val || 0}</span>,
            },
            {
              title: 'Monthly CTC',
              dataIndex: 'monthly_ctc',
              key: 'monthly_ctc',
              width: 140,
              align: 'right',
              render: (val) => <span className="font-mono">{formatINR(val || 0)}</span>,
              sorter: (a, b) => (a.monthly_ctc || 0) - (b.monthly_ctc || 0),
            },
            {
              title: 'Net Salary',
              dataIndex: 'net_salary',
              key: 'net_salary',
              width: 140,
              align: 'right',
              render: (val) => <span className="font-mono text-emerald-600 dark:text-emerald-400">{formatINR(val || 0)}</span>,
              sorter: (a, b) => (a.net_salary || 0) - (b.net_salary || 0),
            },
            {
              title: 'Annual CTC',
              dataIndex: 'annual_ctc',
              key: 'annual_ctc',
              width: 140,
              align: 'right',
              render: (val) => <span className="font-mono text-muted-foreground">{formatINR(val || 0)}</span>,
              sorter: (a, b) => (a.annual_ctc || 0) - (b.annual_ctc || 0),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default HROverview;
